import { Auth0Provider, type AppState } from '@auth0/auth0-react'
import type { PropsWithChildren } from 'react'
import { auth0Config } from '@/config/auth0'
import { AuthController } from '@/components/auth/AuthController'

const DEFAULT_RETURN_TO = '/'

function getRuntimeRedirectUri(): string {
  if (typeof window === 'undefined') {
    return auth0Config.redirectUri
  }
  // IMPORTANT: Keep redirect_uri on the same origin the user is currently on.
  // If the app can be accessed at multiple origins (e.g. with/without www),
  // using a fixed env redirect_uri can cause Auth0 "Invalid state" errors.
  return `${window.location.origin}/auth/callback`
}

function handleRedirect(appState?: AppState): void {
  const target = appState?.returnTo ?? DEFAULT_RETURN_TO
  if (typeof window !== 'undefined') {
    window.history.replaceState({}, document.title, target)
    window.dispatchEvent(new Event('app:navigate'))
  }
}

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const redirectUri = getRuntimeRedirectUri()

return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: auth0Config.audience,
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      // Store the OAuth transaction (state + PKCE verifier) in a first-party
      // cookie instead of sessionStorage. sessionStorage is lost when the flow
      // crosses iOS in-app-browser contexts (e.g. links opened from Outlook),
      // which surfaced as intermittent "Invalid state" errors on the callback.
      useCookiesForTransactions
      onRedirectCallback={handleRedirect}
    >
      <AuthController />
      {children}
    </Auth0Provider>
  )
}
